// Copyright (c) Bentley Systems
import { useEffect } from "react";

/**
 * Debounces a fetch call with automatic AbortController management.
 *
 * After `delayMs` (default 300ms), calls `fetchFn(signal)` where `signal`
 * is from a fresh AbortController. On cleanup (deps change or unmount),
 * the timer is cleared and the in-flight request is aborted.
 *
 * Pass `null` as `fetchFn` to skip the effect entirely (e.g., when a
 * prerequisite like `iTwinId` is not yet available).
 *
 * @example
 * // Re-fetches whenever fetchProjects identity changes (via useCallback deps).
 * useDebouncedFetch(fetchProjects, 300);
 *
 * // Skip when iTwinId is missing.
 * useDebouncedFetch(iTwinId ? fetchCatalogProjects : null, 300);
 */
export function useDebouncedFetch(
  fetchFn: ((signal: AbortSignal) => void) | null,
  delayMs = 300,
) {
  useEffect(() => {
    if (!fetchFn) { return; }

    const abortController = new AbortController();
    const handler = setTimeout(() => {
      fetchFn(abortController.signal);
    }, delayMs);

    return () => {
      clearTimeout(handler);
      abortController.abort();
    };
  }, [fetchFn, delayMs]);
}
